import { useState, useEffect, useMemo, useCallback } from 'react';
import { getValidToken } from '../utils/auth';
import TradingDatePicker from './TradingDatePicker';
import { etTodayStr } from '../utils/tradingCalendar';

const API_URL = process.env.REACT_APP_API_URL || '';

const STATUS_RANK = { Active: 0, Success: 1, Failed: 2, Removed: 3 };
const STATUS_COLOR = {
  Active: '#60a5fa',
  Success: '#22c55e',
  Failed: '#ef4444',
  Removed: '#6b7280',
};

const COLUMNS = [
  { key: 'type',    label: 'Type',        align: 'left'  },
  { key: 'strike',  label: 'Strike',      align: 'right' },
  { key: 'dte',     label: 'DTE',         align: 'right' },
  { key: 'buy',     label: 'Buy',         align: 'right' },
  { key: 'tp',      label: 'TP',          align: 'right' },
  { key: 'sl',      label: 'SL',          align: 'right' },
  { key: 'high',    label: 'H',           align: 'right' },
  { key: 'low',     label: 'L',           align: 'right' },
  { key: 'score',   label: 'Score',       align: 'right' },
  { key: 'news_sentiment', label: 'News', align: 'right' },
  { key: 'added_at',label: 'Time Added',  align: 'right' },
  { key: 'status',  label: 'Status',      align: 'right' },
];

const NEWS_COLOR = { bullish: '#22c55e', bearish: '#ef4444', neutral: '#9ca3af' };

function formatTime(unixMs) {
  if (!unixMs) return '—';
  return new Date(unixMs).toLocaleTimeString('en-US', {
    hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true, timeZone: 'America/New_York',
  });
}

// Contract-by-contract history of everything that has ever entered the SPX
// Best Cheap Contracts widget above, so you can review how each pick played
// out after the fact. Status: Success (high touched TP), Failed (low touched
// SL), Removed (dropped off the widget before either), Active (still open).
export default function ContractStatsHistory() {
  const [date, setDate] = useState(() => etTodayStr());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortCol, setSortCol] = useState('status');
  const [sortDir, setSortDir] = useState('asc');

  const fetchHistory = useCallback(async (d, initial) => {
    try {
      const token = await getValidToken();
      const res = await fetch(`${API_URL}/api/spx-best/history?date=${d}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setRows(await res.json());
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      if (initial) setLoading(false);
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchHistory(date, true);
    const timer = setInterval(() => fetchHistory(date, false), 15000);
    return () => clearInterval(timer);
  }, [date, fetchHistory]);

  const handleSort = (col) => {
    if (col === sortCol) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortCol(col);
      setSortDir(col === 'status' ? 'asc' : 'desc');
    }
  };

  const sorted = useMemo(() => {
    const withRank = rows.map(r => ({ ...r, _statusRank: STATUS_RANK[r.status] ?? 9 }));
    return withRank.sort((a, b) => {
      let va = sortCol === 'status' ? a._statusRank : a[sortCol] ?? '';
      let vb = sortCol === 'status' ? b._statusRank : b[sortCol] ?? '';
      const cmp = typeof va === 'string' ? va.localeCompare(vb) : va - vb;
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [rows, sortCol, sortDir]);

  const counts = useMemo(() => {
    const c = { Active: 0, Success: 0, Failed: 0, Removed: 0 };
    for (const r of rows) if (c[r.status] != null) c[r.status]++;
    return c;
  }, [rows]);

  return (
    <div className="bg-gray-900 rounded-2xl p-5 mb-5">

      {/* Header */}
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <span className="text-xs font-bold px-3 py-1.5 rounded border"
          style={{ backgroundColor: '#111827', color: '#e5e7eb', borderColor: '#374151' }}>
          Contract History
        </span>
        <span className="text-xs text-gray-500">Every contract added to the widget above &middot; kept 30 days</span>

        <div className="flex-1" />

        {Object.entries(counts).map(([status, n]) => (
          <span key={status} className="text-xs font-semibold" style={{ color: STATUS_COLOR[status] }}>
            {status} {n}
          </span>
        ))}

        <TradingDatePicker value={date} onChange={setDate} />
      </div>

      {loading ? (
        <div className="text-gray-500 text-sm text-center py-8">Loading…</div>
      ) : error ? (
        <div className="text-red-400 text-sm text-center py-8">Failed to load: {error}</div>
      ) : rows.length === 0 ? (
        <div className="text-gray-500 text-sm text-center py-8">No contracts recorded for this day</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs" style={{ borderCollapse: 'collapse' }}>
            <thead>
              <tr className="text-gray-500 border-b" style={{ borderColor: '#1f2937' }}>
                {COLUMNS.map(col => (
                  <th key={col.key}
                    onClick={() => handleSort(col.key)}
                    title="Click to sort"
                    className={`text-${col.align} py-2 pr-2 font-medium cursor-pointer select-none`}
                    style={col.key === sortCol ? { color: '#60a5fa' } : undefined}>
                    {col.label}{col.key === sortCol ? (sortDir === 'desc' ? ' ↓' : ' ↑') : ''}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map((r, i) => {
                const isCall = r.type === 'C';
                const typeColor = isCall ? '#22c55e' : '#ef4444';
                return (
                  <tr key={`${r.symbol}-${r.added_at}-${i}`}
                    className="border-b" style={{ borderColor: '#1f2937' }}>
                    <td className="py-1.5 pr-2">
                      <span className="font-semibold px-1.5 py-0.5 rounded"
                        style={{ backgroundColor: typeColor + '22', color: typeColor }}>
                        {isCall ? 'CALL' : 'PUT'}
                      </span>
                    </td>
                    <td className="py-1.5 pr-2 text-right font-mono text-gray-300">{r.strike.toFixed(0)}</td>
                    <td className="py-1.5 pr-2 text-right text-gray-400">{r.dte}</td>
                    <td className="py-1.5 pr-2 text-right font-mono text-gray-300">${r.buy.toFixed(2)}</td>
                    <td className="py-1.5 pr-2 text-right font-mono text-gray-400">${r.tp.toFixed(2)}</td>
                    <td className="py-1.5 pr-2 text-right font-mono text-gray-400">${r.sl.toFixed(2)}</td>
                    <td className="py-1.5 pr-2 text-right font-mono" style={{ color: '#22c55e' }}>${r.high.toFixed(2)}</td>
                    <td className="py-1.5 pr-2 text-right font-mono" style={{ color: '#ef4444' }}>${r.low.toFixed(2)}</td>
                    <td className="py-1.5 pr-2 text-right font-mono text-gray-300">{r.score.toFixed(1)}</td>
                    <td className="py-1.5 pr-2 text-right">
                      {r.news_sentiment ? (
                        <span className="font-semibold" style={{ color: NEWS_COLOR[r.news_sentiment] || '#9ca3af' }}
                          title={`News sentiment when this contract was added: ${r.news_sentiment}`}>
                          {r.news_sentiment === 'bullish' ? '▲' : r.news_sentiment === 'bearish' ? '▼' : '•'}
                        </span>
                      ) : (
                        <span className="text-gray-600">—</span>
                      )}
                    </td>
                    <td className="py-1.5 pr-2 text-right text-gray-400">{formatTime(r.added_at)}</td>
                    <td className="py-1.5 pr-2 text-right">
                      <span className="font-semibold px-1.5 py-0.5 rounded"
                        style={{ backgroundColor: STATUS_COLOR[r.status] + '22', color: STATUS_COLOR[r.status] }}>
                        {r.status}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
